/**
 * `useBuilderPricing()` — React hook that fetches the live Pal Builder
 * pricing config from `/api/pal/pricing` and exposes a `quote(...)`
 * helper for the SendToGame / Box delivery surfaces.
 *
 * Cached at module level so the multiple consumers (BuilderSendSection +
 * SendFromBoxButton + the in-builder cost preview) share one fetch per
 * mount instead of hitting the API three times.
 *
 * Falls back to the historical hardcoded defaults if the network call
 * fails or hasn't returned yet, so the UI never shows "—" coins.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  tier_4_cost:           2500,
  tier_3_cost:           1750,
  tier_2_cost:           1000,
  tier_1_cost:            500,
  tier_0_cost:            250,
  tier_neg_cost:          250,
  active_skill_cost:      250,
  high_level_threshold:    65,
  high_level_surcharge:  2500,
  // Minimum coin cost per delivery — floor applied AFTER all
  // line-items. Prevents Lv-1 + no-passive builds from costing 0.
  min_delivery_cost:      500,
  // Extra fee when the Lucky/Shiny toggle is on in the Builder — makes
  // the pal appear as a shiny in-game. Independent of the "Lucky" passive.
  lucky_variant_cost:    1500,
  // Species-tier surcharges — added as line items (see /api/pal/pricing `tiers`).
  world_tree_min_cost:   5000,
  new_pal_min_cost:      2000,
};

let _cache = null;        // last good pricing dict
let _tiers = null;        // normalized-species-name → 'world_tree' | 'new_pals'
let _inflight = null;     // dedupe concurrent fetches

// Mirrors the backend's `_normalize_species` — strips any trailing
// "(...)" variant suffix so "Zenara & Astralym (Gym Phase 2)" hits the
// same floor as "Zenara & Astralym".
function normalizeSpecies(name) {
  return String(name || "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim()
    .toLowerCase();
}

async function fetchPricing() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/pal/pricing")
    .then(({ data }) => {
      _cache = { ...DEFAULTS, ...(data?.pricing || {}) };
      const t = data?.tiers || {};
      _tiers = {};
      (t.world_tree || []).forEach((n) => { _tiers[normalizeSpecies(n)] = "world_tree"; });
      (t.new_pals || []).forEach((n) => { _tiers[normalizeSpecies(n)] = "new_pals"; });
      return _cache;
    })
    .catch(() => DEFAULTS)
    .finally(() => { _inflight = null; });
  return _inflight;
}

/** Species premium for the pal being delivered (0 when untiered). Added
 *  as a line item, not a floor — mirrors the backend. World Tree pals are
 *  also 1.0 pals, so they pay BOTH taxes. */
export function palTierFloor(palName, pricing) {
  if (!palName || !_tiers) return 0;
  const p = pricing || DEFAULTS;
  const tier = _tiers[normalizeSpecies(palName)];
  const newPal = Number(p.new_pal_min_cost ?? DEFAULTS.new_pal_min_cost);
  if (tier === "world_tree") return newPal + Number(p.world_tree_min_cost ?? DEFAULTS.world_tree_min_cost);
  if (tier === "new_pals")   return newPal;
  return 0;
}

/** Which tax bucket a pal falls in: 'world_tree' | 'new_pals' | null. */
export function palTierName(palName) {
  if (!palName || !_tiers) return null;
  return _tiers[normalizeSpecies(palName)] || null;
}

export function passiveCostFromPricing(tier, pricing) {
  const p = pricing || DEFAULTS;
  if (tier >= 4)  return Number(p.tier_4_cost ?? DEFAULTS.tier_4_cost);
  if (tier === 3) return Number(p.tier_3_cost ?? DEFAULTS.tier_3_cost);
  if (tier === 2) return Number(p.tier_2_cost ?? DEFAULTS.tier_2_cost);
  if (tier === 1) return Number(p.tier_1_cost ?? DEFAULTS.tier_1_cost);
  if (tier === 0) return Number(p.tier_0_cost ?? DEFAULTS.tier_0_cost);
  return Number(p.tier_neg_cost ?? DEFAULTS.tier_neg_cost);
}

/** Compute the total delivery cost given a passives list, level, and
 *  active-skill count. `passives` accepts either {rank} or {tier}. */
export function quoteDeliveryCost({ passives = [], level = 1, activeSkillCount = 0, isLucky = false, pricing, palName = null }) {
  const p = pricing || DEFAULTS;
  const passSum = (passives || []).reduce((s, x) => s + passiveCostFromPricing((x.rank ?? x.tier ?? 0), p), 0);
  const skillSum = (Number(p.active_skill_cost ?? DEFAULTS.active_skill_cost)) * Math.max(0, activeSkillCount);
  const threshold = Number(p.high_level_threshold ?? DEFAULTS.high_level_threshold);
  const surcharge = (Number(level) || 0) > threshold ? Number(p.high_level_surcharge ?? DEFAULTS.high_level_surcharge) : 0;
  const luckyFee = isLucky ? Number(p.lucky_variant_cost ?? DEFAULTS.lucky_variant_cost) : 0;
  // Species premium is ADDITIVE (mirrors backend `_calculate_delivery_cost`).
  const speciesExtra = palTierFloor(palName, p);
  // `min_delivery_cost` is the BASE cost — every line item is added on top.
  const base = Number(p.min_delivery_cost ?? DEFAULTS.min_delivery_cost);
  return base + passSum + skillSum + surcharge + luckyFee + speciesExtra;
}

export function useBuilderPricing() {
  const [pricing, setPricing] = useState(_cache || DEFAULTS);
  useEffect(() => {
    let cancelled = false;
    fetchPricing().then((p) => { if (!cancelled) setPricing(p); });
    return () => { cancelled = true; };
  }, []);
  return pricing;
}

/** Force the cache to refresh (called by the admin save flow). */
export function invalidateBuilderPricingCache() {
  _cache = null;
}
